<template>
  <div>
    <label v-if="label" class="label">
      {{ label }}
    </label>
    <div class="input-wrapper">
      <span v-if="$slots.prefix" class="prefix">
        <slot name="prefix" />
      </span>
      <input :id="id" :class="{
               'has-prefix': $slots.prefix,
               'has-suffix': $slots.suffix,
               error: error
             }" :placeholder="placeholder" :required="required" :type="type"
             :value="modelValue === null || modelValue === undefined ? '' : modelValue"
             :step="type === 'number' ? step : undefined" class="base-input" @blur="$emit('blur')"
             @change="emitValue($event, 'change')" @input="emitValue($event, 'input')">
      <font-awesome-icon
        v-if="clearable && modelValue"
        :icon="['fal', 'times']"
        class="clear-icon"
        @click="clearInput"
      />
      <span v-if="$slots.suffix" class="suffix">
        <slot name="suffix" />
      </span>
    </div>
    <div v-if="error" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  modelValue: {
    type: [String, Number, null, undefined],
    required: false,
    default: null,
  },
  id: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  required: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  step: {
    type: [String, Number],
    default: '1',
  },
  clearable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change', 'blur']);

function emitValue (event, eventType) {
  let value = event.target.value;

  if (props.type === 'number') {
    // Tom string → null
    if (value === '') {
      value = null;
    } else {
      value = Number(value);
      // NaN → null
      if (Number.isNaN(value)) {
        value = null;
      }
    }
  }

  if (eventType === 'change') {
    emit('change', value);
  } else {
    emit('update:modelValue', value);
  }
}

function clearInput () {
  emit('update:modelValue', null);
  emit('change', null);
}
</script>

<style scoped>
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.prefix {
  position: absolute;
  left: 8px;
  color: var(--color-text-light, #666);
}

.base-input.has-prefix {
  padding-left: 32px;
}

.base-input {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--color-border, #ccc);
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.base-input:hover {
  border-color: var(--color-primary);
}

.base-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
}

.suffix {
  position: absolute;
  right: 8px;
  color: var(--color-text-light, #666);
  cursor: pointer;
}

.base-input.has-suffix {
  padding-right: 32px;
}

.error-message {
  color: var(--color-danger);
}

.error {
  border-color: var(--color-danger);
}

.label {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  margin-bottom: 0.3rem;
}

.clear-icon {
  color: var(--gray-500);
  cursor: pointer;
  padding: 8px;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: auto;
}

.clear-icon:hover {
  color: var(--gray-700);
}
</style>
